import { RARE_ITEMS, RARE_ITEM_COUNT } from "@redchain/contracts";
import { u8 } from "./bytes.js";
import type { SaveIssue } from "./errors.js";
import { BAG_ITEM_CAPACITY, F, PC_ITEM_CAPACITY, TERMINATOR, W } from "./layout.js";
import type { ItemSlot } from "./types.js";

export interface ItemListLocation {
  countOffset: number;
  listOffset: number;
  capacity: number;
}

export const BAG_LIST: ItemListLocation = { countOffset: F.numBagItems, listOffset: F.bagItems, capacity: BAG_ITEM_CAPACITY };
export const PC_LIST: ItemListLocation = { countOffset: F.numBoxItems, listOffset: F.boxItems, capacity: PC_ITEM_CAPACITY };

/**
 * Reads an item list (count byte, (id, qty) pairs, 0xFF terminator). Reading stops at the
 * count, the terminator or the capacity, whichever comes first; anomalies go to `issues`.
 */
export function readItemList(b: Uint8Array, loc: ItemListLocation, issues?: SaveIssue[]): ItemSlot[] {
  const count = u8(b, loc.countOffset);
  if (count > loc.capacity) issues?.push({ offset: loc.countOffset, message: `item count ${count} exceeds capacity ${loc.capacity}` });
  const n = Math.min(count, loc.capacity);
  const out: ItemSlot[] = [];
  for (let i = 0; i < n; i++) {
    const off = loc.listOffset + i * 2;
    const id = u8(b, off);
    if (id === TERMINATOR) {
      issues?.push({ offset: off, message: `item list terminated after ${i} of ${count} entries` });
      break;
    }
    out.push({ id, qty: u8(b, off + 1) });
  }
  return out;
}

/** Writes an item list the way AddItemToInventory_/RemoveItemFromInventory leave it. */
export function writeItemList(b: Uint8Array, loc: ItemListLocation, items: ItemSlot[]): void {
  if (items.length > loc.capacity) throw new RangeError(`${items.length} item slots exceed capacity ${loc.capacity}`);
  b[loc.countOffset] = items.length;
  items.forEach((it, i) => {
    if (it.id <= 0 || it.id === TERMINATOR || it.id > 0xff) throw new RangeError(`invalid item id ${it.id}`);
    if (it.qty < 1 || it.qty > 0xff) throw new RangeError(`invalid item quantity ${it.qty}`);
    b[loc.listOffset + i * 2] = it.id;
    b[loc.listOffset + i * 2 + 1] = it.qty;
  });
  b[loc.listOffset + items.length * 2] = TERMINATOR;
}

type ReadWram = (addr: number, len: number) => Uint8Array;

function readWramList(readWram: ReadWram, countAddr: number, listAddr: number, capacity: number): ItemSlot[] {
  const count = u8(readWram(countAddr, 1), 0);
  const n = Math.min(count, capacity);
  const raw = readWram(listAddr, n * 2);
  const out: ItemSlot[] = [];
  for (let i = 0; i < n; i++) {
    const id = u8(raw, i * 2);
    if (id === TERMINATOR) break;
    out.push({ id, qty: u8(raw, i * 2 + 1) });
  }
  return out;
}

export function readBagFromWram(readWram: ReadWram): ItemSlot[] {
  return readWramList(readWram, W.wNumBagItems, W.wBagItems, BAG_ITEM_CAPACITY);
}

export function readPcItemsFromWram(readWram: ReadWram): ItemSlot[] {
  return readWramList(readWram, W.wNumBoxItems, W.wBoxItems, PC_ITEM_CAPACITY);
}

/**
 * ROM patch 3 keeps balls, TMs and HMs out of the bag list: a quantity byte per ball and TM and a bit
 * per HM (rom/qol). The game moves any that sit in the list into these at every save load.
 */
export const POCKET_BALL_IDS = [0x01, 0x02, 0x03, 0x04, 0x08] as const; // Master, Ultra, Great, Poké, Safari
export const NUM_TMS = 50;
export const NUM_HMS = 5;
export const TM01 = 0xc9;
export const HM01 = 0xc4;

export interface PocketBytes {
  tm: Uint8Array;
  hm: number;
  balls: Uint8Array;
}

/** Pocket contents as item slots (an HM counts 1). */
export function pocketSlots(p: PocketBytes): ItemSlot[] {
  const out: ItemSlot[] = [];
  POCKET_BALL_IDS.forEach((id, i) => {
    const qty = p.balls[i] ?? 0;
    if (qty > 0) out.push({ id, qty });
  });
  for (let i = 0; i < NUM_TMS; i++) {
    const qty = p.tm[i] ?? 0;
    if (qty > 0) out.push({ id: TM01 + i, qty });
  }
  for (let i = 0; i < NUM_HMS; i++) if (p.hm & (1 << i)) out.push({ id: HM01 + i, qty: 1 });
  return out;
}

/** Offset in `b` of the pocket byte holding `itemId`, null for an HM or a non-pocket item. */
export function pocketQuantityOffset(itemId: number): number | null {
  const ball = (POCKET_BALL_IDS as readonly number[]).indexOf(itemId);
  if (ball >= 0) return F.ballQuantities + ball;
  if (itemId >= TM01 && itemId < TM01 + NUM_TMS) return F.tmQuantities + (itemId - TM01);
  return null;
}

export function readPockets(sram: Uint8Array): ItemSlot[] {
  return pocketSlots({
    tm: sram.subarray(F.tmQuantities, F.tmQuantities + NUM_TMS),
    hm: u8(sram, F.hmFlags),
    balls: sram.subarray(F.ballQuantities, F.ballQuantities + POCKET_BALL_IDS.length),
  });
}

export function readPocketsFromWram(readWram: ReadWram): ItemSlot[] {
  return pocketSlots({
    tm: readWram(W.wTmQuantities, NUM_TMS),
    hm: u8(readWram(W.wHmFlags, 1), 0),
    balls: readWram(W.wBallQuantities, POCKET_BALL_IDS.length),
  });
}

const RARE_INDEX_BY_ID = new Map<number, number>(RARE_ITEMS.map((r) => [r.gameId, r.index]));

/** Index into RARE_ITEMS for a game item id, or null if it is not a rare item. */
export function rareIndexOf(itemId: number): number | null {
  return RARE_INDEX_BY_ID.get(itemId) ?? null;
}

/** Combined bag + PC + pocket count of each rare item (§14.5), indexed like RARE_ITEMS. */
export function rareItemTotals(...lists: ItemSlot[][]): number[] {
  const totals = new Array<number>(RARE_ITEM_COUNT).fill(0);
  for (const slot of lists.flat()) {
    const idx = rareIndexOf(slot.id);
    if (idx !== null) totals[idx] = (totals[idx] ?? 0) + slot.qty;
  }
  return totals;
}
